import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
	Typography,
	Card,
	CardHeader,
	CardContent,
	Icon,
	IconButton,
	MenuItem,
	TextField,
	Select
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { DatePicker } from '@material-ui/pickers'
import { ArrowForwardIosOutlined, ArrowBackIosOutlined } from '@material-ui/icons'
import { isEqual, sortBy } from 'lodash'
import moment from 'moment'
import {
	GROOMING_SERVICE_TYPES,
	GROOMING_SERVICE_LABELS,
	VISIT_TYPES,
	FONT_WEIGHTS,
	formatNumsToDollars,
	NON_BREAKING_SPACE
} from '../../utils'
import useGroomingVisit from '../../hooks/useGroomingVisit'
import ActivityAlert from '../Wizards/components/ActivityAlert'
import BoardingAppointmentForm from './BoardingAppointmentForm'
import useSelectedFacility from '../../hooks/useSelectedFacility'
import StyledSelect from '../Common/StyledSelect'
import StyledAlert from '../Common/StyledAlert'

const useStyles = makeStyles((theme) => ({
	mainWrapper: {
		display: 'flex',
		flexDirection: 'column',
		gap: '.8rem'
	},
	formWrapper: {
		display: 'flex',
		flexWrap: 'wrap'
	},
	fieldWrapper: {
		'&:nth-child(odd)': {
			marginRight: '25px'
		},
		display: 'flex',
		flexDirection: 'column',
		maxWidth: 420
	},
	calendarButton: {
		'& svg': {
			fill: theme.palette.primary.main
		}
	},
	calendarHeaderButtons: {
		fill: theme.palette.primary.main
	},
	datePicker: {
		marginTop: 0,
		'& .MuiInputLabel-filled': {
			transform: 'translate(48px, 20px) scale(1)'
		},
		'& .MuiInputLabel-shrink': {
			transform: 'translate(48px, 10px) scale(0.75)'
		},
		'&:nth-child(1)': {
			marginRight: '16px !important'
		}
	},
	days: {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		flex: 1
	},
	day: {
		border: '1px solid #D0CFD2',
		background: 'transparent',
		color: '#4D4C4F',
		marginRight: '8px',
		'&:last-child': {
			marginRight: '0px'
		},
		'&:hover': {
			background: '#6B4AD6',
			color: '#FFFFFF',
			borderColor: '#6B4AD6'
		}
	},
	daySelected: {
		background: '#6B4AD6',
		color: '#FFFFFF',
		borderColor: '#6B4AD6'
	},
	timeBlock: {
		textTransform: 'capitalize'
	},
	nextAvailableDateContainer: {
		fontSize: '0.875rem',
		padding: '.5rem 0',
		'& span': {
			fontWeight: FONT_WEIGHTS.Bold
		}
	},
	groomingFormContainer: {
		display: 'flex',
		gap: '1rem',
		marginTop: '1.5rem',
		'& .MuiFilledInput-input': {
			paddingBottom: '6px'
		},
		'& > div': { flex: 1, display: 'flex', flexDirection: 'column' }
	}
}))

const StartCalendar = (props) => (
	<IconButton onClick={() => props.setIsStartCalendarOpen(true)}>
		<Icon
			className="far fa-calendar-alt"
			style={{
				fontSize: '1.325rem'
			}}
		/>
	</IconButton>
)

// const EndCalendar = (props) => (
// 	<IconButton onClick={() => props.setIsEndCalendarOpen(true)}>
// 		<Icon
// 			className="far fa-calendar-alt"
// 			style={{
// 				fontSize: '1.325rem'
// 			}}
// 		/>
// 	</IconButton>
// )

const CreateVisitTypeCard = (props) => {
	const classes = useStyles()
	const timeBlocks = ['morning', 'afternoon', 'evening']

	const [occurences, setOccurences] = useState(0)
	const [isStartCalendarOpen, setIsStartCalendarOpen] = useState(false)
	const [selectedGroomType, setSelectedGroomType] = useState('none')
	const [selectedStandaloneAddonId, setSelectedStandaloneAddonId] = useState('none')
	const [selectedStandaloneAddonChoiceId, setSelectedStandaloneAddonChoiceId] = useState('none')
	const [generalGroomingAvailability, setGeneralGroomingAvailability] = useState({ none: '' })
	const { getGeneralAvailability, fetchingAvailability } = useGroomingVisit()
	const { facility } = useSelectedFacility()

	let { visit, setVisit } = props

	const isAddOnGroomingVisitOnly =
		visit?.type === VISIT_TYPES.AddonOnly && visit?.subtype === VISIT_TYPES.Grooming

	useEffect(() => {
		if (isAddOnGroomingVisitOnly) {
			return
		}
		const fetchGeneralAvailability = async () => {
			const data = await getGeneralAvailability({ grooming_service_type: selectedGroomType })
			if (data && data.errors) {
				setGeneralGroomingAvailability((prev) => {
					return {
						...prev,
						[selectedGroomType]: (
							<div className={classes.nextAvailableDateContainer}>
								<span>There was an error fetching availability.</span>
							</div>
						)
					}
				})
				return
			}
			if (data && data.shifts && data.shifts.length) {
				if (data.shifts.length === 0) {
					setGeneralGroomingAvailability((prev) => {
						return {
							...prev,
							[selectedGroomType]: (
								<div className={classes.nextAvailableDateContainer}>
									<span>No availability for the next 4 months</span>
								</div>
							)
						}
					})
				} else {
					const { shifts } = data
					const [firstShift] = shifts
					const { date } = firstShift
					const formatedDate = moment(date).format('dddd, MMMM Do, YYYY')
					setGeneralGroomingAvailability((prev) => {
						return {
							...prev,
							[selectedGroomType]: (
								<div className={classes.nextAvailableDateContainer}>
									<span>Next available date</span>: {formatedDate}
								</div>
							)
						}
					})
				}
			}
		}

		if (selectedGroomType === 'none' || generalGroomingAvailability[selectedGroomType] !== undefined) {
			return
		}

		fetchGeneralAvailability()
	}, [selectedGroomType, getGeneralAvailability, setGeneralGroomingAvailability, isAddOnGroomingVisitOnly])

	useEffect(() => {
		let { startDate, endDate, datesBetween, daysOfTheWeek, type } = visit

		if (type === VISIT_TYPES.Daycare || type === VISIT_TYPES.CompatibilitySession) {
			if (startDate && endDate && datesBetween.length > 0) {
				let dayOccurences = daysOfTheWeek.filter((dayOfTheWeek) => dayOfTheWeek.selected)

				let datesThatMatch = datesBetween.filter((date) => {
					let dayOfTheWeek = moment(date).format('dddd').toLowerCase()
					return dayOccurences.some((dayOccurence) => dayOfTheWeek === dayOccurence.value)
				})

				setOccurences(datesThatMatch.length)

				if (!isEqual(sortBy(datesThatMatch), sortBy(visit.dates))) {
					setVisit({
						...visit,
						dates: datesThatMatch
					})
				}
			}
		} else {
			if (!isEqual(sortBy(visit.datesBetween), sortBy(visit.dates))) {
				setVisit({
					...visit,
					dates: visit.datesBetween
				})
			}
		}
	}, [visit])

	const groomingServiceOptions = useMemo(() => {
		const opts = [
			<MenuItem value={'none'} key="none">
				Select a groom service
			</MenuItem>
		]
		Object.values(GROOMING_SERVICE_TYPES)
			.filter((value) => value !== 'nail_trim')
			.forEach((value) => {
				opts.push(
					<MenuItem value={value} key={value}>
						{GROOMING_SERVICE_LABELS[value]}
					</MenuItem>
				)
			})
		return opts
	}, [])

	const availableGroomingStandaloneOptions = (() => {
		const opts = []
		if (!facility) {
			return opts
		}

		const available =
			facility.addOnTypes?.filter(
				(ao) =>
					ao.standalone_service === true &&
					ao.revenue_type === VISIT_TYPES.Grooming &&
					ao.active === true
			) || []

		if (available.length > 0) {
			opts.push(
				<MenuItem value="none" key="none">
					Select an enrichment
				</MenuItem>
			)
		}

		available.forEach((ao) => {
			const { name, _id } = ao
			opts.push(
				<MenuItem value={_id} key={_id}>
					{name}
				</MenuItem>
			)
		})

		return opts
	})()

	const availableGroomingStandaloneChoices = (() => {
		if (selectedStandaloneAddonId === 'none') {
			return []
		}
		const opts = []

		const addonDetails = facility.addOnTypes.find((ao) => ao._id === selectedStandaloneAddonId)
		if (addonDetails && addonDetails.addon_choices?.length > 0) {
			opts.push(
				<MenuItem value="none" key="none">
					Select an enrichment choice
				</MenuItem>
			)
			addonDetails.addon_choices.forEach((ao) => {
				const { name, id, upcharge = 0, description = '' } = ao
				const formatUpcharge = formatNumsToDollars(upcharge)
				const formattedName =
					upcharge > 0
						? `${name}${NON_BREAKING_SPACE}${NON_BREAKING_SPACE}${NON_BREAKING_SPACE}+${formatUpcharge}`
						: name
				opts.push(
					<MenuItem value={id} key={id} title={description}>
						{formattedName}
					</MenuItem>
				)
			})
		}

		return opts
	})()

	const handleStandaloneAddonChange = (e) => {
		const addonId = e?.target?.value || 'none'
		setSelectedStandaloneAddonId(addonId)
		if (addonId === 'none') {
			setSelectedStandaloneAddonChoiceId('none')
		}
		setVisit((prev) => {
			return {
				...prev,
				standalone_addon_id: addonId === 'none' ? null : addonId,
				standalone_addon_choice_id: null
			}
		})
	}

	const handleStandalonAddonChoiceChange = (e) => {
		const addonChoiceId = e?.target?.value || 'none'
		setSelectedStandaloneAddonChoiceId(addonChoiceId)
		setVisit((prev) => {
			return {
				...prev,
				standalone_addon_choice_id: addonChoiceId === 'none' ? null : addonChoiceId
			}
		})
	}

	const handleGroomTypeChange = useCallback(
		(event) => {
			const groomingType = event?.target?.value || ''

			setSelectedGroomType(groomingType)

			if (groomingType === GROOMING_SERVICE_TYPES.addon_only) {
				setVisit((prev) => ({
					...prev,
					type: VISIT_TYPES.AddonOnly,
					subtype: VISIT_TYPES.Grooming,
					startDate: moment()
				}))
			} else {
				setVisit((prev) => ({
					...prev,
					type: VISIT_TYPES.Grooming,
					subtype: '',
					grooming_service_type: event.target.value === 'none' ? '' : event.target.value
				}))
			}
		},
		[setSelectedGroomType, setVisit]
	)

	const setType = (type) => {
		const updatedVisit = {
			...visit,
			startDate: null,
			endDate: null,
			type,
			time_block: null,
			early_dropoff: false,
			extended_pickup: false,
			first_day_daycare: false,
			last_day_daycare: false,
			sharing_kennels: false
		}

		setVisit(updatedVisit)
	}

	const setStartDate = (date) => {
		setVisit({
			...visit,
			startDate: date
		})

		setIsStartCalendarOpen(false)
	}

	const setEndDate = (date) => {
		setVisit({
			...visit,
			endDate: date
		})
	}

	// const setSharingKennels = useCallback(
	// 	(sharingKennels) => {
	// 		console.log('sharing kennels', sharingKennels)
	// 		setVisit((prev) => ({ ...prev, sharing_kennels: sharingKennels }))
	// 	},
	// 	[setVisit]
	// )

	const setFirstDayDaycare = useCallback(
		(firstDayDaycare) => {
			setVisit((prev) => {
				const updatedVisit = { ...prev, first_day_daycare: firstDayDaycare }
				if (!firstDayDaycare) {
					updatedVisit['early_dropoff'] = false
				}

				return updatedVisit
			})
		},
		[setVisit]
	)

	const setLastDayDaycare = useCallback(
		(lastDayDaycare) => {
			setVisit((prev) => {
				const updatedVisit = { ...prev, last_day_daycare: lastDayDaycare }
				if (!lastDayDaycare) {
					updatedVisit['extended_pickup'] = false
				}

				return updatedVisit
			})
		},
		[setVisit]
	)

	const setFirstDayDropOffTime = useCallback(
		(firstDayDropOffTime) => {
			setVisit((prev) => ({ ...prev, early_dropoff: firstDayDropOffTime }))
		},
		[setVisit]
	)

	const setLastDayPickUpTime = useCallback(
		(lastDayPickUpTime) => {
			setVisit((prev) => ({ ...prev, extended_pickup: lastDayPickUpTime }))
		},
		[setVisit]
	)

	return (
		<Card style={{ marginTop: '24px', marginBottom: '24px' }}>
			<CardHeader title="Appointment type" />
			<CardContent>
				{visit.type === VISIT_TYPES.Grooming || isAddOnGroomingVisitOnly ? (
					<div className={classes.formWrapper} style={{ flexDirection: 'column' }}>
						<div className={classes.fieldWrapper}>
							<Typography variant="overline">Select Appointment Type</Typography>
							<TextField
								id="household-select"
								variant="filled"
								select
								label="Appointment Type"
								disabled={true}
								style={{ textTransform: 'capitalize' }}
								value={visit.type}
								onChange={(e) => setType(e.target.value)}>
								<MenuItem key="groom" value="grooming" style={{ textTransform: 'capitalize' }}>
									Grooming
								</MenuItem>
								<MenuItem key="groom" value="addon_only" style={{ textTransform: 'capitalize' }}>
									Grooming - Enrichment Only
								</MenuItem>
							</TextField>
						</div>
						<div>
							{visit.type === VISIT_TYPES.Grooming ? (
								<div>
									<Typography variant="overline">Date Checker</Typography>
									<div>Check grooming appointment availability at a glance.</div>
								</div>
							) : null}
							<div className={classes.groomingFormContainer}>
								<div>
									<Typography variant="overline">
										{isAddOnGroomingVisitOnly ? 'Date' : 'Ideal Date'}
									</Typography>
									<DatePicker
										disableToolbar
										open={isStartCalendarOpen}
										onOpen={() => setIsStartCalendarOpen(true)}
										onClose={() => setIsStartCalendarOpen(false)}
										InputProps={{
											startAdornment: (
												<StartCalendar setIsStartCalendarOpen={setIsStartCalendarOpen} />
											)
										}}
										variant="inline"
										format="MM/DD/YYYY"
										margin="normal"
										id="start-date"
										label="Date"
										inputVariant="filled"
										value={visit.startDate}
										onChange={setStartDate}
										className={classes.datePicker}
										disablePast
										disabled={isAddOnGroomingVisitOnly}
										rightArrowIcon={
											<ArrowForwardIosOutlined className={classes.calendarHeaderButtons} />
										}
										leftArrowIcon={
											<ArrowBackIosOutlined className={classes.calendarHeaderButtons} />
										}
									/>
								</div>
								<div>
									<Typography variant="overline">Service Type</Typography>
									<Select
										value={selectedGroomType}
										onChange={handleGroomTypeChange}
										variant="outlined">
										{groomingServiceOptions}
									</Select>
									{isAddOnGroomingVisitOnly ? (
										<div style={{ marginTop: '.5rem' }}>
											{availableGroomingStandaloneOptions.length > 0 ? (
												<StyledSelect
													header="Select Enrichment Type"
													value={selectedStandaloneAddonId}
													onChange={handleStandaloneAddonChange}>
													{availableGroomingStandaloneOptions}
												</StyledSelect>
											) : (
												<StyledAlert type="info" text="None are available at this time" />
											)}
											{availableGroomingStandaloneChoices.length > 0 ? (
												<div style={{ marginTop: '.5rem' }}>
													<StyledSelect
														header="Select Enrichment Choice"
														value={selectedStandaloneAddonChoiceId}
														onChange={handleStandalonAddonChoiceChange}>
														{availableGroomingStandaloneChoices}
													</StyledSelect>
												</div>
											) : null}
										</div>
									) : null}
								</div>
							</div>
							{fetchingAvailability ? (
								<ActivityAlert
									text={`Fetching soonest available date for a ${selectedGroomType}`}
								/>
							) : null}
							{generalGroomingAvailability[selectedGroomType] !== undefined &&
							generalGroomingAvailability[selectedGroomType] > '' &&
							!fetchingAvailability ? (
								<div>{generalGroomingAvailability[selectedGroomType]}</div>
							) : null}
						</div>
					</div>
				) : null}
				{visit.type !== VISIT_TYPES.Grooming && !isAddOnGroomingVisitOnly ? (
					<div className={classes.mainWrapper}>
						<div className={classes.fieldWrapper}>
							<Typography variant="overline">Select Appointment Type</Typography>
							<TextField
								id="household-select"
								variant="filled"
								select
								label="Appointment Type"
								style={{ textTransform: 'capitalize' }}
								value={visit.type}
								onChange={(e) => setType(e.target.value)}>
								{facility
									? [...Object.keys(facility.services)]
											.filter((s) => s !== 'compatibility' && s !== 'training')
											.filter(
												(service) =>
													service !== VISIT_TYPES.Grooming &&
													service !== VISIT_TYPES.AddonOnly
											)
											.map((service) => (
												<MenuItem
													key={service}
													value={service}
													style={{ textTransform: 'capitalize' }}>
													{service.split('_').join(' ')}
												</MenuItem>
											))
									: null}
							</TextField>
						</div>
						<div className={classes.formWrapper}>
							{visit.type === VISIT_TYPES.Boarding ? (
								<BoardingAppointmentForm
									visit={visit}
									onStartDateSelected={setStartDate}
									onEndDateSelected={setEndDate}
									onFirstDayDaycareSelected={setFirstDayDaycare}
									onLastDayDaycareSelected={setLastDayDaycare}
									onFirstDayDropOffTimeSelected={setFirstDayDropOffTime}
									onLastDayPickUpTimeSelected={setLastDayPickUpTime}
									// onSharingKennelsSelected={setSharingKennels}
								/>
							) : (
								<div className={classes.fieldWrapper}>
									<Typography variant="overline">On this day</Typography>
									<DatePicker
										disableToolbar
										open={isStartCalendarOpen}
										onOpen={() => setIsStartCalendarOpen(true)}
										onClose={() => setIsStartCalendarOpen(false)}
										InputProps={{
											startAdornment: (
												<StartCalendar setIsStartCalendarOpen={setIsStartCalendarOpen} />
											)
										}}
										variant="inline"
										format="MM/DD/YYYY"
										margin="normal"
										id="start-date"
										label={'Start Date'}
										inputVariant="filled"
										value={visit.startDate}
										onChange={setStartDate}
										className={classes.datePicker}
										disablePast
										rightArrowIcon={
											<ArrowForwardIosOutlined className={classes.calendarHeaderButtons} />
										}
										leftArrowIcon={
											<ArrowBackIosOutlined className={classes.calendarHeaderButtons} />
										}
									/>
								</div>
							)}
							{visit.type === VISIT_TYPES.CompatibilitySession ? (
								<div className={classes.fieldWrapper}>
									<Typography variant="overline" style={{ fontSize: '12px' }}>
										At this timeblock
									</Typography>
									<TextField
										id="time-select"
										variant="filled"
										select
										label="Time Block"
										style={{ textTransform: 'capitalize' }}
										value={visit.time_block || ''}
										onChange={(e) => setVisit({ ...visit, time_block: e.target.value })}
										InputProps={{
											className: visit.time_block ? classes.selected : ''
										}}>
										{timeBlocks.map((timeBlock) => (
											<MenuItem
												className={classes.timeBlock}
												key={timeBlock}
												value={timeBlock}>
												{timeBlock}
											</MenuItem>
										))}
									</TextField>
								</div>
							) : null}
							{visit.type === VISIT_TYPES.CompatibilitySession ? (
								<div className={classes.fieldWrapper}>
									<Typography variant="overline" style={{ fontSize: '12px' }}>
										Drop-off & Pick-up
									</Typography>
									{facility.compatibility_day_messaging ? (
										<div>
											<Typography style={{ color: '#4D4C4F' }}>
												{`Arrival ${
													facility.compatibility_day_messaging.morning || 'N/A'
												}`}
												<br />
												{`Pick-up ${
													facility.compatibility_day_messaging.afternoon || 'N/A'
												}`}
											</Typography>
										</div>
									) : null}
								</div>
							) : null}
						</div>
					</div>
				) : null}
			</CardContent>
		</Card>
	)
}

export default CreateVisitTypeCard
